import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore
import styles from "../../FilterBar.module.scss";

type ButtonsNavProps = {
  filterOptions: string[];
  activeFilter: number | null;
  handleBtnClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number, label?: string) => void;
  style?: string;
  button_style?: string;
};

export const ButtonsNav: React.FC<ButtonsNavProps> = ({ filterOptions, activeFilter, handleBtnClick, button_style = 'dark' }) => {
  const getTranslateXValue = useCallback((element: HTMLElement): number => {
    const style = window.getComputedStyle(element);
    const transform = style.transform;

    if (transform === "none") return 0;

    const matrix = new DOMMatrix(transform);
    return matrix.m41;
  }, []);
  const [gliderStyle, setGliderStyle] = useState<{ width: number; left: number }>({
    width: 0,
    left: 0,
  });
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragStarted: false,
    startX: 0,
    startLeft: 0,
    showLeftFade: false,
    showRightFade: false,
    canDrag: false,
  });

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const btnListRef = useRef<HTMLDivElement | null>(null);
  const navWrapperRef = useRef<HTMLUListElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const justDragged = useRef(false);
  const DRAG_THRESHOLD = 5;

  const updateGlider = useCallback(() => {
    const activeBtn = activeFilter !== null ? buttonRefs.current[activeFilter] : null;
    const wrapper = navWrapperRef.current;

    if (activeBtn && wrapper) {
      const btnRect = activeBtn.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      setGliderStyle({
        width: btnRect.width,
        left: btnRect.left - wrapperRect.left,
      });
    }

  }, [activeFilter]);

  const updateFadeState = useCallback(() => {
    const wrapper = navWrapperRef.current;
    const container = btnListRef.current;

    if (!wrapper || !container) return;

    const currentLeft = getTranslateXValue(wrapper);
    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const maxLeft = -(wrapperWidth - availableWidth);
    setDragState((prev) => ({
      ...prev,
      showLeftFade: currentLeft < 0,
      showRightFade: currentLeft > maxLeft,
    }));
  }, [getTranslateXValue]);

  const checkOverflow = useCallback(() => {
    const container = btnListRef.current;
    const wrapper = navWrapperRef.current;

    if (!container || !wrapper) return;

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;

    const canDrag = wrapperWidth > availableWidth;

    setDragState((prev) => ({
      ...prev,
      canDrag,
      showLeftFade: canDrag ? prev.showLeftFade : false,
      showRightFade: canDrag ? prev.showRightFade : false,
    }));

    if (canDrag) {
      updateFadeState();
    }
  }, [updateFadeState]);

  const handleResize = useCallback(() => {
    const wrapper = navWrapperRef.current;
    const container = btnListRef.current;

    if (!wrapper || !container) return;

    const currentLeft = getTranslateXValue(wrapper);

    checkOverflow();

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const canDrag = wrapperWidth > availableWidth;

    if (canDrag) {
      const maxLeft = -(wrapperWidth - availableWidth);
      const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);

      // If current position would show empty space, adjust it
      if (currentLeft + wrapperGap < maxLeft) {
        wrapper.classList.add(styles.smooth);
        wrapper.style.transform = `translateX(${maxLeft}px)`;
        updateFadeState();

        // Remove smooth class after animation
        setTimeout(() => {
          wrapper.classList.remove(styles.smooth);
        }, 300);
      } else {
        updateFadeState();
      }
    } else {
      // If no drag needed, reset position to 0
      if (currentLeft !== 0) {
        wrapper.classList.add(styles.smooth);
        wrapper.style.transform = "translateX(0px)";

        setTimeout(() => {
          wrapper.classList.remove(styles.smooth);
        }, 300);
      }
    }

    // Update glider after potential position changes
    updateGlider();
  }, [checkOverflow, updateFadeState, updateGlider, getTranslateXValue]);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!dragState.canDrag) return;

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const wrapper = navWrapperRef.current;

      if (!wrapper) return;

      const currentLeft = getTranslateXValue(wrapper);

      setDragState((prev) => ({
        ...prev,
        startX: clientX,
        startLeft: currentLeft,
        isDragging: false,
        dragStarted: false,
      }));

      wrapper.style.transition = "";
      e.preventDefault();
    },
    [dragState.canDrag, getTranslateXValue],
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (dragState.startX === 0) return;

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - dragState.startX;
      const wrapper = navWrapperRef.current;
      const container = btnListRef.current;

      if (!wrapper || !container) return;

      // On touch: preventDefault from first move so Safari iOS doesn't treat horizontal swipe as back/forward
      const isTouch = "touches" in e;
      if (isTouch) {
        e.preventDefault();
      }

      // Check if we've moved enough to consider this a drag
      if (!dragState.dragStarted && Math.abs(deltaX) > DRAG_THRESHOLD) {
        setDragState((prev) => ({
          ...prev,
          dragStarted: true,
          isDragging: true,
        }));
      }

      if (dragState.dragStarted) {
        const newLeft = dragState.startLeft + deltaX;
        const availableWidth = container.parentElement!.clientWidth;
        const wrapperWidth = wrapper.scrollWidth;
        const maxLeft = -(wrapperWidth - availableWidth);

        const constrainedLeft = Math.max(maxLeft, Math.min(0, newLeft));
        const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);

        wrapper.style.transform = `translateX(${constrainedLeft ? constrainedLeft - wrapperGap : constrainedLeft}px)`;
        updateFadeState();
      }

      if (!isTouch && dragState.dragStarted) {
        e.preventDefault();
      }
    },
    [dragState.startX, dragState.startLeft, dragState.dragStarted, DRAG_THRESHOLD, updateFadeState],
  );

  const handleDragEnd = useCallback(() => {
    if (dragState.startX === 0) return;

    const wrapper = navWrapperRef.current;
    if (wrapper) {
      wrapper.classList.add(styles.smooth);
      setTimeout(() => {
        if (wrapper) wrapper.classList.remove(styles.smooth);
      }, 300);
    }

    // If a drag was in progress, set justDragged to true
    if (dragState.dragStarted) {
      justDragged.current = true;
      // Reset justDragged after a short delay to avoid blocking real clicks
      setTimeout(() => {
        justDragged.current = false;
      }, 100);
    }

    setDragState((prev) => ({
      ...prev,
      startX: 0,
      isDragging: false,
      dragStarted: false,
    }));
  }, [dragState.startX, dragState.dragStarted]);

  const handleBtnClickWithDrag = useCallback(
    (e: React.MouseEvent<HTMLLIElement>, index: number, label?: string) => {
      // Prevent tab activation if we just finished dragging
      if (dragState.isDragging || justDragged.current) {
        e.preventDefault();
        justDragged.current = false; // Reset after blocking
        return;
      }

      handleBtnClick(e, index, label);
    },
    [dragState.isDragging, handleBtnClick],
  );

  useEffect(() => {
    resizeObserverRef.current?.disconnect();

    resizeObserverRef.current = new ResizeObserver(() => {
      handleResize();
    });

    const activeBtn = activeFilter !== null ? buttonRefs.current[activeFilter] : null;
    const navList = btnListRef.current;

    if (activeBtn) {
      resizeObserverRef.current.observe(activeBtn);
    }
    if (navList) {
      resizeObserverRef.current.observe(navList);
    }

    handleResize();

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [activeFilter, filterOptions, handleResize]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: TouchEvent) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (!filterOptions || filterOptions.length === 0) return;

    requestAnimationFrame(() => {
      checkOverflow();
      updateGlider();
    });
  }, [filterOptions, activeFilter, checkOverflow, updateGlider]);

  // Scroll active tab into view if needed
  useEffect(() => {
    if (!dragState.canDrag) return;
    const wrapper = navWrapperRef.current;
    const container = btnListRef.current;
    const activeBtn = activeFilter !== null ? buttonRefs.current[activeFilter] : null;

    if (!wrapper || !container || !activeBtn) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    const currentLeft = getTranslateXValue(wrapper);
    const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);
    const containerLeft = containerRect.left;
    const containerRight = containerRect.right;
    const btnLeft = btnRect.left;
    const btnRight = btnRect.right;
    let newLeft = currentLeft;
    if (btnLeft < containerLeft) newLeft += containerLeft - btnLeft + wrapperGap;
    else if (btnRight > containerRight) newLeft -= btnRight - containerRight;

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const maxLeft = -(wrapperWidth - availableWidth);
    newLeft = Math.max(maxLeft, Math.min(0, newLeft));
    if (newLeft !== currentLeft) {
      wrapper.classList.add(styles.smooth);
      wrapper.style.transform = `translateX(${newLeft ? newLeft - wrapperGap : newLeft}px)`;
      setTimeout(() => {
        wrapper.classList.remove(styles.smooth);
        updateFadeState();
      }, 400);
    }
  }, [activeFilter, dragState.canDrag, updateFadeState, getTranslateXValue]);

  if (!filterOptions) return null;

  return (
    <div className={cn(styles.filterBtnsNavigation)}>
      <nav className={styles.btnsNavWrapper} role="tablist" aria-label="filter-button-list">
        <div
          className={cn(styles.filterBtnsContainer, {
            [styles.draggable]: dragState.canDrag,
            [styles.showLeftFade]: dragState.showLeftFade,
            [styles.showRightFade]: dragState.showRightFade,
          })}
          ref={btnListRef}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            cursor: dragState.canDrag ? "grab" : "default",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <ul
            ref={navWrapperRef}
            className={cn(styles.filterBtnsNavList, {
              [styles.smooth]: false,
              [styles.overflowing]: dragState.canDrag,
            })}
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
            }}
          >
            {filterOptions.length > 1 && (
              <li
                key="all"
                onClick={(e) => handleBtnClickWithDrag(e, 0)}
                className={cn(styles.filterBtnNavItem, button_style, {
                  [styles.active]: activeFilter === 0,
                })}
                style={{ flexShrink: 0 }}
              >
                <button
                  type="button"
                  ref={(el) => {
                    buttonRefs.current[0] = el;
                  }}
                  className={cn(styles.filterBtnNavButton, button_style)}
                  role="tab"
                  aria-selected={activeFilter === 0}
                  aria-controls={`filter-${0}`}
                  id={`filter-${0}`}
                >
                  Show All
                </button>
              </li>
            )}
            {filterOptions.map((entry, i) => {
              const index = filterOptions.length > 1 ? i + 1 : i;
              return (
                <li
                  key={entry || index}
                  onClick={(e) => handleBtnClickWithDrag(e, index, entry)}
                  className={cn(styles.filterBtnNavItem, button_style,{
                    [styles.active]: activeFilter === index,
                  })}
                  style={{ flexShrink: 0 }}
                >
                  <button
                    type="button"
                    ref={(el) => {
                      buttonRefs.current[index] = el;
                    }}
                    className={cn(styles.filterBtnNavButton, button_style)}
                    role="tab"
                    aria-selected={activeFilter === index}
                    aria-controls={`filter-${index}`}
                    id={`filter-${index}`}
                  >
                    {entry}
                  </button>
                </li>
              );
            })}

            <li
              className={cn(styles.glider)}
              style={{
                width: gliderStyle.width,
                transform: `translateX(${gliderStyle.left}px)`,
                position: "absolute",
                pointerEvents: "none",
              }}
            />
          </ul>
        </div>
      </nav>
    </div>
  );
};
